// ` To trigger a change event on an input element, you can set the value
//   property on the element with the native setter and dispatch the event
//   (setting value directly is not working because it uses React’s
//   overridden setter): `
//
// https://stackoverflow.com/questions/23892547/what-is-the-best-way-to-trigger-change-or-input-event-in-react-js
export const triggerInputChange = (
  node: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement,
  value: string
) => {
  if (typeof window === 'undefined') return

  const nodeProto = Object.getPrototypeOf(node)
  const setValue = Object.getOwnPropertyDescriptor(nodeProto, 'value')?.set

  // only process the change on elements we know have a value setter in their constructor
  if (
    [HTMLInputElement, HTMLSelectElement, HTMLTextAreaElement].indexOf(
      nodeProto.constructor
    ) > -1 &&
    setValue
  ) {
    const event = new Event('input', { bubbles: true })

    setValue.call(node, value)
    node.dispatchEvent(event)
  }
}

export const getClosestInteractiveElement = (
  element: Element | EventTarget | null
) => {
  if (!element || !(element instanceof HTMLElement)) {
    return null
  }

  return element?.closest(
    'a, button, label, [role="button"], input[type="button"], input[type="submit"], input[type="reset"], input[type="checkbox"], input[type="radio"], input[type="file"], [contenteditable="true"]'
  )
}

/**
 * Schedules a callback to be executed on the next animation frame.
 *
 * @example
 * const cancel = nextFrame(() => {
 *   // Code to run after the next paint
 * });
 * // To cancel the scheduled callback:
 * cancel();
 */
export const nextFrame = (cb: (...args: unknown[]) => void, numFrames = 1) => {
  let id: number

  const frame = (remaining: number) => {
    if (remaining > 1) {
      id = requestAnimationFrame(() => frame(remaining - 1))
    } else {
      id = requestAnimationFrame(() => cb())
    }
  }

  frame(numFrames)

  return () => cancelAnimationFrame(id)
}
